import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Textarea, Select } from '@components';
import { EmailTemplate, CreateEmailTemplateData, UpdateEmailTemplateData } from '../types';
import { useTemplateModal } from '../hooks/useTemplateModal';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEmailTemplateData | UpdateEmailTemplateData) => Promise<void>;
  template?: EmailTemplate | null;
  loading?: boolean;
}

export const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  template,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { formData, errors, handleInputChange, handleSubmit, resetForm } = useTemplateModal({
    onSubmit,
    loading,
  });

  useEffect(() => {
    if (template) {
      resetForm({
        title: template.title,
        subject: template.subject,
        body: template.body,
        status: template.status,
      });
    } else {
      resetForm({
        title: '',
        subject: '',
        body: '',
        status: 'active',
      });
    }
  }, [template, isOpen, resetForm]);

  const isEdit = !!template;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEdit ? t('communications.editEmailTemplate') : t('communications.createEmailTemplate')
      }
      size="lg"
    >
      <form onSubmit={e => handleSubmit(e, ['title', 'subject', 'body'])} className="space-y-6">
        <div>
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('communications.templateTitle')}
          </label>
          <Input
            id="title"
            type="text"
            value={(formData.title as string) || ''}
            onChange={e => handleInputChange('title', e.target.value)}
            placeholder={t('communications.enterTemplateTitle')}
            errorText={errors.title}
            required
          />
        </div>

        <div>
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('communications.emailSubject')}
          </label>
          <Input
            id="subject"
            type="text"
            value={(formData.subject as string) || ''}
            onChange={e => handleInputChange('subject', e.target.value)}
            placeholder={t('communications.enterEmailSubject')}
            errorText={errors.subject}
            required
          />
        </div>

        <div>
          <label
            htmlFor="body"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('communications.emailBody')}
          </label>
          <Textarea
            id="body"
            value={(formData.body as string) || ''}
            onChange={e => handleInputChange('body', e.target.value)}
            placeholder={t('communications.enterEmailBodyContent')}
            rows={8}
            errorText={errors.body}
            required
          />
        </div>

        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('common.status')}
          </label>
          <Select
            id="status"
            value={(formData.status as string) || 'active'}
            onChange={value => handleInputChange('status', Array.isArray(value) ? value[0] : value)}
            options={[
              { value: 'active', label: t('communications.active') },
              { value: 'inactive', label: t('common.inactive') },
            ]}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            {t('communications.cancel')}
          </Button>
          <Button type="submit" variant="gradient-multi" loading={loading} disabled={loading}>
            {isEdit ? t('communications.updateTemplate') : t('communications.createTemplate')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
